import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { IndexedDBService, KeyType } from "./indexed-db.service";
import { catchError, share, switchMap, take, tap, map, timeout } from "rxjs/operators";
import { MapType, Maps } from "@design/models/ui-state.model";
import Util from "@design/containers/draw-area/threejs/helpers/Utility";
import { EngineService } from "@design/containers/draw-area/threejs/threejs.service";
import { arrayBufferToBase64 } from "src/app/helpers/utils";
import { BehaviorSubject, Observable, of, timer } from "rxjs";
import { ProjectsService } from "src/app/modules/projects/projects.service";
import { Store } from "@ngrx/store";
import { DesignState } from "@design/store/design.reducer";
import { fromMetaDataSelectors } from "@design/store/meta-data";
import { AuthService } from "src/app/helpers/auth.service";
import { getHideLoaderHeaders } from "src/app/helpers/interceptors/loader.interceptor";
import { openDialog } from "@lib-ui/lib/common/utils/openDialog";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { fromAdditionalToolsActions } from "@design/store/additional-tools";
import { SaveStatus } from "@design/models/save-status.model";
import { environment } from "src/environments/environment";
import { AdditionalToolsApi } from "@design/api/additional-tools-api";

@Injectable({
  providedIn: 'root'
})
export class HeightImageService {
  mapDataWorker: Worker;
  siteWidthInMeter: number = 0;
  private siteConfig: any = { meterPerPixel: 110 / 2000 };
  mapUrl: BehaviorSubject<string> = new BehaviorSubject('');
  mapImageUrl: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  crn: string = '';
  selectedMap!: string;
  checkHasDSM: any;
  isMapUpdate = false
  /**
   * In-memory pinned satellite/aerial raster (raw base64, no data-URI prefix) for
   * the currently-open quote revision. When set, it is preferred over live
   * Google/NearMap imagery so the design does not drift when Google refreshes maps.
   */
  private pinnedGroundMap: string | null = null;
  /** Map-type discriminator (KeyType) the current pin is keyed by. */
  private pinnedMapTypeKey: string = '';
  /**
   * Guards all pin behaviour so it only engages for an explicitly-resolved design
   * load (via resolvePinnedGroundMap). Prevents incidental map loads in other
   * contexts from pinning under stale localStorage quote IDs.
   */
  private pinningContextActive = false;
  /** Ensures we save/re-pin at most once per load (the image can be applied twice). */
  private pinBackfilledThisLoad = false;
  /** ~12 MB decoded cap to avoid pinning pathologically large rasters. */
  private readonly MAX_PINNED_MAP_BYTES = 12 * 1024 * 1024;
  /** Fall back to the live map fetch if the pinned-raster read stalls. */
  private readonly PINNED_FETCH_TIMEOUT_MS = 8000;

  updateMapUrl(value: string) {
    this.mapUrl.next(value);
  }

  getMapUrl() {
    return this.mapUrl.asObservable();
  }
  constructor(private store: Store<DesignState>,
    private engServ: EngineService,
    private indexedDBService: IndexedDBService,
    private projectsService: ProjectsService,
    private authService: AuthService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private additionalToolsApi: AdditionalToolsApi,
    private http: HttpClient,) {
    // @ts-ignore
    this.mapDataWorker = new Worker(new URL('@design/workers/map-data-download.worker', import.meta.url), { type: 'module' });
    this.getHeighDataFromWorker();
  }

  /**
   * Fetches a fresh signed image URL for the current map from the API.
   * Used by PVCAD export so the URL is always valid (signed URLs expire;
   * the cached base64 is not suitable for sending as a URL reference).
   */
  fetchGroundMapImageUrl(): Observable<string | null> {
    if (!this.crn) {
      this.crn = localStorage.getItem('crn') as string;
    }
    if (!this.crn || !this.selectedMap) return of(null);

    const mapType = this.getMapType(this.selectedMap, !Util.nonDsm);
    let url: string;
    if (mapType === KeyType.NearMap) {
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${this.crn}&map_option=NEARMAPS&resource_type=image`;
    } else if (mapType === KeyType.GoogleSolar) {
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${this.crn}&map_option=GOOGLEMAPS&resource_type=image&rgbtif=true`;
    } else {
      url = `${environment.newSiteDataApiEndpoint}site/additional-info?crn=${this.crn}&map_option=GOOGLEMAPS&resource_type=image`;
    }

    return this.http.get<any>(url, { headers: getHideLoaderHeaders() }).pipe(
      map((res: any) => res?.data ?? null),
      catchError(() => of(null)),
    );
  }

  getSiteData(maps: any) {
    this.store.select(fromMetaDataSelectors.getSiteData).pipe(take(1)).subscribe(
      (data: any) => {
        if (data) {
          console.info('getSiteData with data:', data);
          
          const isGoogleSolar = maps?.replace(/ /g, '').toLowerCase().includes('googlesolar');
          const isGoogleMap = maps?.replace(/ /g, '').toLowerCase().includes('googlemap');
          
          const radius = isGoogleSolar ? data.radius : Math.min(data.radius, 100);
          const mpp = isGoogleMap ? (data.height * data?.meterPerPixel) / 2000 : (radius * 2) / 2000;
          
          this.siteWidthInMeter = data.height * data?.meterPerPixel;
          this.siteConfig = { ...data, meterPerPixel: mpp };
          Util.siteConfig = this.siteConfig;
        }
      },
      (error: any) => console.error('get site data error:', error)
    );
  
    if (maps !== 'none') {
      const checkHasDSM = !maps?.replace(/ /g, '').toLowerCase().includes('googlemap');
      this.selectedMap = maps;
      this.store.dispatch(fromAdditionalToolsActions.setMapLoadingStatus({ mapLoadingStatus: SaveStatus.OUTDATED }));
      if (maps == 'Custom Image (No Map) ') {
        this.store.dispatch(fromAdditionalToolsActions.setMapLoadingStatus({ mapLoadingStatus: SaveStatus.SAVED }));
        this.store.dispatch(fromAdditionalToolsActions.setSceneRestoreStatus({ sceneRestoreStatus: SaveStatus.SAVED }));
      }
      const mapType = this.getMapTypeFromMaps(maps);
      if (mapType) {
        // Resolve the per-revision pinned raster first, then continue the normal
        // map-load. applyMapImage() prefers the pin over live Google/NearMap imagery
        // so a saved design never gets displaced when Google refreshes its maps.
        this.resolvePinnedGroundMap(maps, () => {
          this.isHeightDataPresentInIndexedDB(mapType, checkHasDSM);
        });
      }
    }
  }

  /** True for custom-image/no-map selections, which are never pinned. */
  private isCustomImageMap(maps: string): boolean {
    return !maps || maps === 'none' || maps.replace(/ /g, '').toLowerCase().includes('customimage');
  }

  /** A pin can only be keyed when we have a concrete quote revision in context. */
  private isGroundMapPinEligible(): boolean {
    const crn = this.crn || (localStorage.getItem('crn') as string);
    const quoteId = this.authService.getItem('quote_id', 'localStorage');
    const qrn = this.authService.getItem('qrn', 'localStorage');
    const revNo = this.authService.getItem('revision_number', 'localStorage');
    return !!(crn && quoteId && qrn && revNo !== null && revNo !== undefined && `${revNo}`.trim() !== '');
  }

  /** Strip any data-URI prefix so the value matches the raw-base64 map pipeline. */
  private normalizePinnedImage(img: any): string {
    if (!img || typeof img !== 'string') return '';
    return img.replace(/^data:image\/(png|jpe?g);base64,/, '').trim();
  }

  /**
   * Fetches the pin  l
   * ned raster for the current revision, stores it in memory, then
   * invokes `done()` to continue the load. Custom-image/no-map, explicit map updates
   * (which re-pin afterwards), ineligible contexts, and API failures all fall back
   * to the normal live map-load path.
   */
  private resolvePinnedGroundMap(maps: string, done: () => void): void {
    this.pinnedGroundMap = null;
    // Key the pin per map type so switching Google Map/Solar/NearMap never serves
    // a stale raster. Captured here (before any early return) so an explicit
    // map-update can re-pin under the correct type.
    this.pinnedMapTypeKey = this.getMapTypeFromMaps(maps) || '';
    // Pin behaviour is allowed for this load only when we have a real, eligible
    // design context (covers both reuse and re-pin-after-update).
    this.pinningContextActive = !this.isCustomImageMap(maps) && this.isGroundMapPinEligible();
    this.pinBackfilledThisLoad = false;

    if (this.isCustomImageMap(maps) || this.isMapUpdate || !this.isGroundMapPinEligible()) {
      done();
      return;
    }

    this.additionalToolsApi.getPinnedGroundMap(this.pinnedMapTypeKey).pipe(
      timeout(this.PINNED_FETCH_TIMEOUT_MS),
      take(1)
    ).subscribe(
      (resp: any) => {
        this.pinnedGroundMap = this.normalizePinnedImage(resp?.image) || null;
        if (this.pinnedGroundMap) {
          console.info('Using pinned ground-map raster for this revision');
        }
        done();
      },
      (err: any) => {
        console.warn('getPinnedGroundMap failed; falling back to live map fetch', err);
        this.pinnedGroundMap = null;
        done();
      }
    );
  }

  /**
   * Single chokepoint for applying a downloaded map raster to the scene. Prefers the
   * pinned raster when one exists (and we are not mid map-update); otherwise applies
   * the freshly-downloaded raster and pins it (backfill for legacy designs / new
   * revisions, or re-pin after an explicit map update).
   */
  private applyMapImage(downloadedBase64: string): void {
    // Outside a resolved design context, behave exactly like the legacy path.
    if (!this.pinningContextActive) {
      this.updateMapUrl(downloadedBase64);
      return;
    }

    const isUpdate = this.isMapUpdate;

    // `pinnedGroundMap` is resolved once at load start and never mutated mid-load,
    // so every apply during a load is deterministic. When a pin exists (and we are
    // not explicitly updating) it always wins, even if the legacy flow applies the
    // image more than once (e.g. cached IndexedDB image then a fresh Google fetch).
    if (this.pinnedGroundMap && !isUpdate) {
      this.updateMapUrl(this.pinnedGroundMap);
      return;
    }

    // No server pin (legacy design / new revision -> backfill) or an explicit
    // map update (-> re-pin). Display the fresh raster and persist it once.
    this.updateMapUrl(downloadedBase64);
    if (!this.pinBackfilledThisLoad) {
      this.pinBackfilledThisLoad = true;
      this.maybePinGroundMap(downloadedBase64);
      // The map-update intent is consumed once the fresh raster has been applied.
      if (isUpdate) {
        this.isMapUpdate = false;
      }
    }
  }

  /**
   * Persists the given raster as this revision's pin when eligible. Non-blocking and
   * best-effort: the live image is already on screen, so failures are only logged.
   */
  private maybePinGroundMap(base64: string): void {
    if (!base64 || typeof base64 !== 'string') return;
    if (this.isCustomImageMap(this.selectedMap)) return;
    if (!this.isGroundMapPinEligible()) return;

    const approxBytes = Math.ceil((base64.length * 3) / 4);
    if (approxBytes > this.MAX_PINNED_MAP_BYTES) {
      console.warn('Skipping ground-map pin: raster exceeds size cap', approxBytes);
      return;
    }

    this.additionalToolsApi.savePinnedGroundMap(base64, this.pinnedMapTypeKey).pipe(take(1)).subscribe(
      () => console.info('Pinned ground-map raster for this revision'),
      (err: any) => console.warn('savePinnedGroundMap failed (non-blocking)', err)
    );
  }
  
  /**
   * The `map_type` key ('googlesolar' | 'googlemaps' | 'nearmaps', or '' when the
   * current map is not pinnable) the open revision's ground-map raster is pinned
   * under — i.e. the exact value savePinnedGroundMap / getPinnedGroundMap use.
   * Exposed so the PVCAD export can embed it as a durable getBackgroundImage
   * `map_type` that resolves the same stored file.
   */
  getGroundMapImageKey(): string {
    return this.pinnedMapTypeKey || '';
  }

  getMapTypeFromMaps(maps: string): KeyType | null {
    switch (maps) {
      case Maps.GoogleMap:
      case MapType.GOOGLESOLAR:
      case Maps.GoogleSolar:
        return maps?.replace(/ /g, '').toLowerCase().includes('googlemap') ? KeyType.GoogleMap : KeyType.GoogleSolar;
      case Maps.NearMap:
      case MapType.NEARMAPS:
        return KeyType.NearMap;
      default:
        return null;
    }
  }
  


  // crn :: getHeight data from indexedDB
  getHeightDataFromIndexedDb(
    crn: string,
    mapType: string,
    currentSelectedMap: string,
    fromProjectDetails: boolean = false
  ) {
    console.info('getHeightDataFromIndexedDb:', crn, mapType, currentSelectedMap, fromProjectDetails);
  
    this.indexedDBService.getItem(crn).pipe(take(1)).subscribe(indexData => {
      const parsedData = indexData ? JSON.parse(indexData) : null;
  
      const handleMapData = (image: string, height: any = undefined) => {
        if (!fromProjectDetails) {
          Util.heightsArray = height;
          Util.nonDsm = mapType === KeyType.GoogleMap;
          this.applyMapImage(image);
          console.info('set height data for mapType:', mapType);
        }
      };
  
      if (parsedData && this.indexedDBService.hasMapData(crn, parsedData, mapType, KeyType.Height) && mapType !== KeyType.GoogleMap
          && Util.isUsableHeightsArray(parsedData[mapType][KeyType.Height])) {
        handleMapData(parsedData[mapType].image, parsedData[mapType].height);
      } else if (parsedData && this.indexedDBService.hasMapData(crn, parsedData, mapType, KeyType.Image) && mapType === KeyType.GoogleMap) {
        handleMapData(parsedData[mapType].image);
      } else {
        const DSMval = currentSelectedMap.toLowerCase() === Maps.GoogleMap.toLowerCase()
          ? false
          : currentSelectedMap.toLowerCase() === Maps.GoogleSolar.toLowerCase();
  
        const defaultGMapOption = currentSelectedMap.replace(/ /g, '').toLowerCase().includes('google')
          ? 'GOOGLEMAPS'
          : '';
  
        console.info('getHeightDataFromIndexedDb getMapHeight:', DSMval, defaultGMapOption);
        this.engServ.getMapHeight(this.mapDataWorker, DSMval.toString(), defaultGMapOption);
      }
    });
  }
  

  isHeightDataPresentInIndexedDB(currentSelectedMap: string, checkHasDSM: any, fromProjectDetails: boolean = false) { // project details
    if (!this.crn) {
      this.crn = localStorage.getItem('crn') as string;
    }
    const mapType = this.getMapType(currentSelectedMap, checkHasDSM);
    this.indexedDBService.getItem(this.crn).pipe(take(1)).subscribe(indexData => {
      const parsedData = indexData ? JSON.parse(indexData) : null;
      const needsHeight = mapType != KeyType.GoogleMap;
      const hasCachedHeight = !!(parsedData && this.indexedDBService.hasMapData(this.crn, parsedData, mapType, KeyType.Height));
      const hasCachedImage = !!(parsedData && this.indexedDBService.hasMapData(this.crn, parsedData, mapType, KeyType.Image));

      // this cache is per quote and per browser, so a bad raster breaks the quote on this device forever
      const cachedHeightUsable = hasCachedHeight
        && Util.isUsableHeightsArray(parsedData[mapType][KeyType.Height]);
      if (needsHeight && hasCachedHeight && !cachedHeightUsable) {
        console.warn('Evicting unusable cached DSM raster and refetching', this.crn, mapType);
        this.indexedDBService.deleteItem(this.crn);
      }

      if (needsHeight && cachedHeightUsable && hasCachedImage && !this.isMapUpdate) {
        console.info('Height data is present in indexed db', mapType, currentSelectedMap, fromProjectDetails);
        this.getHeightDataFromIndexedDb(this.crn, mapType, currentSelectedMap, fromProjectDetails);
      } else if (!needsHeight && hasCachedImage && !this.isMapUpdate) {
        console.info('Image data is present in indexed db', mapType, currentSelectedMap, fromProjectDetails);
        this.getHeightDataFromIndexedDb(this.crn, mapType, currentSelectedMap, fromProjectDetails);
      } else {
        console.info('Height data is not present in indexed db', currentSelectedMap, checkHasDSM);
        this.checkHeightDataStatus(currentSelectedMap, checkHasDSM);
      }
    });
  }

  getMapType(map: string, checkHasDSM: any){
    const mapType = map?.replace(/ /g, '').toLowerCase()?.includes('nearmap') ? KeyType.NearMap : ((map?.replace(/ /g, '').toLowerCase()?.includes('googlemap') && (!checkHasDSM || checkHasDSM.toString() == 'false')) ? KeyType.GoogleMap : KeyType.GoogleSolar);
    return mapType;
  }

  checkHeightDataStatus(
    currentSelectedMap: string,
    checkHasDSM: any
  ) {
    checkHasDSM = (checkHasDSM == undefined || checkHasDSM == null) ? false : checkHasDSM;

    const headers = getHideLoaderHeaders();
    console.info('checkHeightDataStatus called with:', currentSelectedMap);

    const mapOption = currentSelectedMap?.replace(/ /g, '').toLowerCase().includes('near')
      ? 'NEARMAPS'
      : 'GOOGLEMAPS';

    console.info('checkHeightDataStatus mapOption:', mapOption);

    if (checkHasDSM && checkHasDSM !== 'false') {
      const subscription = timer(0, 2000)
        .pipe(
          switchMap(() => this.projectsService.checkHeightData(headers, mapOption)),
          tap((res: any) => {
            if (res.isDsmDataDownloaded && (mapOption === 'NEARMAPS' || mapOption === 'GOOGLEMAPS')) {
              this.callHeightApi(checkHasDSM, mapOption);
            }
          }),
          share()
        )
        .subscribe((res) => {
          if (res.isDsmDataDownloaded) subscription.unsubscribe();
        });
    } else {
      // no DSM here, so nonDsm must be true or every getHeight call throws
      Util.nonDsm = true;
      Util.heightsArray = undefined;
      // Non-DSM Google Maps image-only path: prefer the pinned raster and skip the
      // redundant live fetch entirely when a pin exists for this revision.
      if (this.pinnedGroundMap && !this.isMapUpdate) {
        this.applyMapImage(this.pinnedGroundMap);
      } else {
        this.engServ.getGoogleMap(this.mapDataWorker, false);
      }
    }
  }

  callHeightApi(hasDsm: any = '', currentSelectedMap: string = '') {
    console.info('callHeightApi called with :', hasDsm, currentSelectedMap);
  
    // routing below needs hasDsm, geometry code reads the inverse
    const hasDsmFlag = hasDsm.toString() === 'true';
    Util.nonDsm = !hasDsmFlag;
    this.selectedMap = this.getMapType(currentSelectedMap, hasDsm);
  
    console.info(
      'callHeightApi called getMapHeight/getGoogleMap :',
      hasDsm,
      currentSelectedMap,
      hasDsmFlag
    );
  
    if (hasDsmFlag) {
      this.engServ.getMapHeight(this.mapDataWorker, hasDsm, currentSelectedMap);
    } else {
      Util.heightsArray = undefined;
      this.engServ.getGoogleMap(this.mapDataWorker, false);
    }
  }

  getHeighDataFromWorker(): void {
    this.mapDataWorker.onmessage = ({ data }) => {
      const mapType = this.getMapType(this.selectedMap, !Util.nonDsm);
      console.info('getHeighDataFromWorker onmessage called with :', mapType, this.selectedMap);
      if (data.action === 'download-height-data-completed') {
        if (this.crn && this.selectedMap) {
          this.indexedDBService.getItem(this.crn).pipe(take(1)).subscribe(indexData => {
            if (indexData && this.indexedDBService.hasMapData(this.crn, JSON.parse(indexData), mapType, KeyType.Height) && !this.isMapUpdate) {
              console.info('cache data found for :', mapType)
              this.getHeightDataFromIndexedDb(this.crn, mapType, this.selectedMap)
              if (mapType == KeyType.NearMap) {
                this.engServ.getNearMap(this.mapDataWorker);
              } else if (mapType === KeyType.GoogleMap) {
                this.engServ.getGoogleMap(this.mapDataWorker, false);
              } else {
                this.engServ.getGoogleMap(this.mapDataWorker, true);
              }
            } else {
              console.info('set cache data for :', mapType)
              this.indexedDBService.setMapData(this.crn, indexData, mapType, KeyType.Height, data.response);
              Util.heightsArray = data.response;
              Util.nonDsm = false;
              if (mapType == KeyType.NearMap) {
                this.engServ.getNearMap(this.mapDataWorker);
              } else if (mapType === KeyType.GoogleMap) {
                this.engServ.getGoogleMap(this.mapDataWorker, false);
              } else {
                this.engServ.getGoogleMap(this.mapDataWorker, true);
              }
            }
          })
        }
      } else if (data.action === 'download-height-data-error') {
        this.errorDialog(data.error);
        this.authService.enableDesignLoader.next({ val: true, action: 'stop' });
        console.error('Found error to get DSM data. Please reload');
      } else if (['download-google-map-image-completed', 'download-google-map-image-error', 'download-near-map-image-completed', 'download-near-map-image-error'].includes(data.action)) {
        if (this.crn && this.selectedMap) {
          this.indexedDBService.getItem(this.crn).pipe(take(1)).subscribe(indexData => {
            console.info('download image for map :', mapType)//, indexData);
            if (indexData && this.indexedDBService.hasMapData(this.crn, JSON.parse(indexData), mapType, KeyType.Image) && !this.isMapUpdate) {
              this.setDataForDesignPage(JSON.parse(indexData)[mapType].image);
            } else if (['download-google-map-image-completed', 'download-near-map-image-completed'].includes(data.action)) {
              const imageBase64 = arrayBufferToBase64(data.response);
              if (data.imageUrl) {
                this.mapImageUrl.next(data.imageUrl);
              }
              this.indexedDBService.setMapData(this.crn, indexData, mapType, KeyType.Image, imageBase64);
              this.setDataForDesignPage(data, imageBase64, mapType == KeyType.GoogleMap ? false : true);
            } else if (data.action === 'download-google-map-image-error') {
              console.error('get google map image URL error :', data.error);
            }
            else if (data.action === 'download-near-map-image-error') {
              console.error('get near map image URL error :', data.error);
            }
          })
        }
      }
    };
  }

  setDataForDesignPage(data: any, base64Image?: string, checkHasDSM = true) {
    console.info('setDataForDesignPage image :', checkHasDSM)//base64Image;
    if (data.action === 'download-google-map-image-completed') {
      if (!checkHasDSM) { // Google Maps
        Util.heightsArray = undefined;
        Util.nonDsm = true;
      }
      console.log('updating google map :', checkHasDSM)
      this.applyMapImage(base64Image ? base64Image : data); // Prefers the pinned raster; backfills/re-pins otherwise
    } else if (data.action === 'download-google-map-image-error') {
      console.error('get google map image URL error :', data.error);
      this.authService.enableDesignLoader.next({ val: true, action: 'stop' })
    } else if (data.action === 'download-near-map-image-completed') {
      console.log('updating near map')
      this.applyMapImage(base64Image ? base64Image : data); // Prefers the pinned raster; backfills/re-pins otherwise
    } else if (data.action === 'download-near-map-image-error') {
      console.error('get near map image URL error :', data.error);
      this.authService.enableDesignLoader.next({ val: true, action: 'stop' })
    }
  }

  errorDialog(err: string) {
    const data = {
      showYesBtn: false,
      showNoBtn: false,
      title: this.translateMsg("unblToLoadHeight"),
      message: this.translateMsg("unblToLoadHeightError"),
    };
    openDialog(this.dialog, data);
  }

  translateMsg(val: any) {
    let translatedName = ''
    this.translate.get('toastrMsgs.' + val).pipe(take(1)).subscribe(res => {
      translatedName = res;
    })
    return translatedName;
  }

}
